import React, { useEffect, useState, useContext } from 'react';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import DatePicker from '@amzn/meridian/date-picker';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import infoIcon from '@amzn/meridian-tokens/base/icon/info';
import Icon from '@amzn/meridian/icon';
import Toggle from '@amzn/meridian/toggle';
import Box from '@amzn/meridian/box';
import ViewHeadcount from './staticPlan/viewPlan/ssd/headcount/ViewHeadcount';
import CopyToClipboardButton from '../commonComponents/CopyToClipboardButton';
import { chainWalk, parseEpochToDateStringWithOptions } from '../utils/helpers';
import {
  ADMIN_SETTINGS_DAILY_SHIFTS,
  DATE_TIME_FORMAT,
  ERROR_CODES,
  PLAN_STATUS,
  PLANS,
  SECONDS_IN_AN_HOUR,
  SSD_TIMELINE_KEY,
  SSD_TIMELINE_TYPE
} from '../utils/constants';
import ViewOutboundVolumesByBusiness from './staticPlan/common/ssd/outboundVolumes/ViewOutboundVolumesByBusiness';
import ViewOutboundVolumes from './staticPlan/common/ssd/outboundVolumes/ViewOutboundVolumes';
import ViewDispatchVolumes from './staticPlan/common/ssd/ViewDispatchVolumes';
import ViewRates from './staticPlan/common/ssd/ViewRates';
import ViewPlanOverview from './staticPlan/viewPlan/ssd/overview/ViewPlanOverview';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Button from '@amzn/meridian/button';
import apis from '../utils/apis';
import Toaster from '@amzn/meridian/toaster';
import Alert from '@amzn/meridian/alert';
import {
  getNextQuarterLocalDateTime,
  getNextSSDShiftStartTimeForDailyShifts,
  getShiftEndTimeForShiftStartTime,
  getTimelineForCreatePlan,
  validateAdminSettings
} from './staticPlan/utils';
import { GlobalStateContext } from '../global/context/GlobalStateContext';
import { getAdminSettings } from '../utils/AdminSettings';
import { getStationCode } from '../utils/networkUtil';

const VIEW_NAME = {
  OVERVIEW: 'Overview',
  OB_VOLUME: 'OB Volume',
  DISPATCH_VOLUME: 'Dispatch Volume',
  HEADCOUNT: 'Headcount',
  MSR_RATES: 'MSR Rates'
};

const ViewPlanSSD = ({
  selectedOfdDate,
  onChangeSelectedOfdDate,
  selectedPlanType,
  onChangeSelectedPlanType,
  responseDataForGivenPlan,
  runPlan,
  getPlan,
  isSsdPendingPlanCreationExists,
  isPlanCancelEnabled,
  cancelPlanId
}) => {
  const data = responseDataForGivenPlan[selectedPlanType] ? JSON.parse(responseDataForGivenPlan[selectedPlanType]) : {};
  const [isShowPrevious, setIsShowPrevious] = useState(false);
  const [selectedView, setSelectedView] = useState(VIEW_NAME.OVERVIEW);
  const [adminSettings, setAdminSettings] = useState(null);
  const [toasts, setToasts] = useState([]);
  const onCloseToast = (id) => setToasts(toasts.filter((t) => t.id !== id));
  const [planCancelEnabled, setPlanCancelEnabled] = useState(isPlanCancelEnabled);
  const [isGroceryEnabled, setIsGroceryEnabled] = useState(false);
  const [isRushEnabled, setIsRushEnabled] = useState(false);
  const { setAsyncError } = useContext(GlobalStateContext);

  useEffect(() => {
    (async () => {
      const settings = await getAdminSettings(getStationCode());
      setAdminSettings(settings);
      const groceryEnabled = chainWalk(() => settings.grocery_enabled, false);
      const rushEnabled = chainWalk(() => settings.rush_enabled, false);
      setIsGroceryEnabled(groceryEnabled);
      setIsRushEnabled(rushEnabled);
    })();
    if (data.status === PLAN_STATUS.PENDING_USER_INPUT) {
      getPlan(selectedPlanType);
    }
  }, []);

  const viewComponent = (() => {
    switch (selectedView) {
      case VIEW_NAME.OVERVIEW:
        return <ViewPlanOverview plan={data} />;
      case VIEW_NAME.OB_VOLUME:
        return isGroceryEnabled || isRushEnabled ? (
          <ViewOutboundVolumesByBusiness isEditable={false} plan={data} />
        ) : (
          <ViewOutboundVolumes isEditable={false} plan={data} />
        );
      case VIEW_NAME.DISPATCH_VOLUME:
        return <ViewDispatchVolumes isEditable={false} plan={data} />;
      case VIEW_NAME.HEADCOUNT:
        return <ViewHeadcount plan={data} />;
      case VIEW_NAME.MSR_RATES:
        return <ViewRates isEditable={false} plan={data} />;
      default:
        return <div />;
    }
  })();

  const abortOtherUserPlan = async (planId) => {
    await updatePlanStatusForPlan(PLAN_STATUS.CANCELLED, planId);
    setToasts([]);
    setPlanCancelEnabled(false);
  };

  const updatePlanStatusForPlan = async (status, planId) => {
    await apis['UPDATE_PLAN_STATUS'](
      {
        body: {
          planId: planId,
          status
        }
      },
      () => setAsyncError(ERROR_CODES.SHIFT_PLAN_UPDATE_ERROR)
    );
  };

  const onRunPlan = () => {
    const isValidAdminSettings = validateAdminSettings(adminSettings);
    if (isValidAdminSettings.requiredKeysPresent) {
      if (planCancelEnabled) {
        return setToasts(
          toasts.concat({
            id: Math.random(),
            message: `Plan generation is in progress. Cancel current plan to create a new one.`,
            showCancelButton: true,
            planId: cancelPlanId
          })
        );
      }
      if (ADMIN_SETTINGS_DAILY_SHIFTS in adminSettings) {
        const shiftPlanHorizonStartTime = getNextQuarterLocalDateTime(adminSettings);
        const shiftPlanHorizonEndTime = getShiftEndTimeForShiftStartTime(shiftPlanHorizonStartTime);
        runPlan(selectedPlanType, undefined, undefined, { shiftPlanHorizonStartTime, shiftPlanHorizonEndTime });
      } else {
        const timeline = getTimelineForCreatePlan(adminSettings, false);
        const shiftPlanHorizonStartTime = timeline.find(
          ({ key, type }) => key === SSD_TIMELINE_KEY.PLAN && type === SSD_TIMELINE_TYPE.START
        ).timestamp;
        const shiftPlanHorizonEndTime = timeline.find(
          ({ key, type }) => key === SSD_TIMELINE_KEY.PLAN && type === SSD_TIMELINE_TYPE.END
        ).timestamp;
        runPlan(selectedPlanType, undefined, undefined, { shiftPlanHorizonStartTime, shiftPlanHorizonEndTime });
      }
    } else {
      setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please configure/upload valid admin settings from settings page for properties: ${isValidAdminSettings.invalidKeys}`
        })
      );
    }
  };
  const createdAtEpoch = chainWalk(() => data.createdAt, null);
  const dataCreatedDisplayText = createdAtEpoch
    ? `Data Created: ${parseEpochToDateStringWithOptions(createdAtEpoch, DATE_TIME_FORMAT)} (${data.lastUpdatedBy}@)`
    : '';
  const isPlanCreationDisabled = !adminSettings || isSsdPendingPlanCreationExists;
  return (
    <Column spacingInset="0 400 400 400" spacing="450">
      <Text type="h500">Shift Plan</Text>
      <Box type="fill" spacingInset="300">
        <Row alignmentHorizontal="justify" spacingInset="200 300 200 300">
          <Row alignmentHorizontal="start" spacingInset="100" spacing="300">
            <Text type="b200">Out For Delivery Date</Text>
            <DatePicker size="small" value={selectedOfdDate} onChange={onChangeSelectedOfdDate} />
            <div style={{ width: '20px' }} />
            <ButtonGroup size="small" value={selectedPlanType} onChange={onChangeSelectedPlanType}>
              <ButtonOption value={PLANS.PRE_SHIFT}>Shift-Level</ButtonOption>
              <ButtonOption value={PLANS.DYNAMIC} disabled={true}>
                Dynamic
              </ButtonOption>
            </ButtonGroup>
          </Row>
          <Text type="b200">{dataCreatedDisplayText}</Text>
        </Row>
      </Box>
      {Object.keys(data).length > 0 ? (
        <Column>
          <Box type="fill" spacingInset="400">
            <Row alignmentHorizontal="justify">
              <React.Fragment>
                <Toaster toasts={toasts} onCloseToast={onCloseToast}>
                  {(toast) => (
                    <Alert toast={true} onClose={toast.onClose} type="error">
                      <div>{toast.message}</div>
                      {toast.showCancelButton && (
                        <Row alignmentHorizontal="center">
                          <Button type="primary" size="small" onClick={() => abortOtherUserPlan(toast.planId, toast)}>
                            Cancel Plan
                          </Button>
                        </Row>
                      )}
                    </Alert>
                  )}
                </Toaster>
              </React.Fragment>
              <Row>
                <Icon tokens={infoIcon} />
                <Column spacing="0">
                  <Text type="h200">Current SSD Plan</Text>
                  <Text type="b100">
                    {`(Plan ID: ${data['planId']}`}
                    <CopyToClipboardButton value={data['planId']} />
                    {')'}
                  </Text>
                </Column>
              </Row>
              <Row spacing="300">
                <Toggle disabled={true} size="medium" checked={isShowPrevious} onChange={setIsShowPrevious}>
                  Show previous
                </Toggle>
                <div />
                <Button type="primary" size="small" onClick={onRunPlan} disabled={isPlanCreationDisabled}>
                  Run New Plan
                </Button>
              </Row>
            </Row>
          </Box>
          <TabGroup value={selectedView} onChange={setSelectedView} type="default" fill="line">
            {Object.entries(VIEW_NAME).map(([_, label]) => {
              return (
                <Tab key={label} value={label}>
                  {label}
                </Tab>
              );
            })}
          </TabGroup>
          {viewComponent}
        </Column>
      ) : (
        <Box type="fill" spacingInset="400">
          <Row alignmentHorizontal="justify">
            <React.Fragment>
              <Toaster toasts={toasts} onCloseToast={onCloseToast}>
                {(toast) => (
                  <Alert toast={true} onClose={toast.onClose} type="error">
                    <div>{toast.message}</div>
                    {toast.showCancelButton && (
                      <Row alignmentHorizontal="center">
                        <Button type="primary" size="small" onClick={() => abortOtherUserPlan(toast.planId, toast)}>
                          Cancel Plan
                        </Button>
                      </Row>
                    )}
                  </Alert>
                )}
              </Toaster>
            </React.Fragment>
            <Row>
              <Icon tokens={infoIcon} />
              <Text type="b200">Plan has not been created</Text>
            </Row>
            <Row spacing="300">
              <Toggle disabled={true} size="medium" checked={isShowPrevious} onChange={setIsShowPrevious}>
                Show previous
              </Toggle>
              <div />
              <Button type="primary" size="small" onClick={onRunPlan} disabled={isPlanCreationDisabled}>
                Run New Plan
              </Button>
            </Row>
          </Row>
        </Box>
      )}
    </Column>
  );
};

export default ViewPlanSSD;
